import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { 
  BarChart3, 
  Globe, 
  Users, 
  TrendingUp,
  Calendar,
  Eye,
  MapPin,
  Clock,
  RefreshCw,
  FileText
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const Analytics = () => {
  const { language } = useLanguage();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [recentVisits, setRecentVisits] = useState([]);
  const [sendingReport, setSendingReport] = useState(false);

  const COLORS = ['#d4af37', '#fbbf24', '#f59e0b', '#d97706', '#b45309'];

  useEffect(() => {
    if (isAdmin()) {
      fetchAnalytics();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const [summaryRes, visitsRes] = await Promise.all([
        api.get('/analytics/summary'),
        api.get('/analytics/recent-visits?limit=20')
      ]);
      setAnalytics(summaryRes.data);
      setRecentVisits(visitsRes.data);
    } catch (error) {
      toast.error('فشل تحميل بيانات التحليلات');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMonthlyReport = async () => {
    try {
      setSendingReport(true);
      await api.post('/analytics/send-monthly-report');
      toast.success('تم إرسال التقرير الشهري بنجاح');
    } catch (error) {
      toast.error('فشل إرسال التقرير');
    } finally {
      setSendingReport(false);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">هذه الصفحة متاحة للمدير فقط</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ar-AE', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCountryFlag = (countryCode) => {
    if (!countryCode) return '🌍';
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  return (
    <div className="space-y-6" dir={language === 'ar' ? 'rtl' : 'ltr'} data-testid="analytics-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-primary" />
            تحليلات الزوار
          </h1>
          <p className="text-muted-foreground mt-1">
            إحصائيات وتحليلات زيارات الموقع
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={fetchAnalytics}
            variant="outline"
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            تحديث
          </Button>
          <Button
            onClick={handleSendMonthlyReport}
            disabled={sendingReport}
            className="bg-primary text-primary-foreground gap-2"
          >
            <FileText className="w-4 h-4" />
            {sendingReport ? 'جاري الإرسال...' : 'إرسال تقرير شهري'}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-white/5 rounded-xl p-6" data-testid="total-visits-card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/20 rounded-lg">
              <Eye className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">إجمالي الزيارات</p>
              <p className="text-2xl font-bold">{analytics?.total_visits || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-white/5 rounded-xl p-6" data-testid="today-visits-card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-500/20 rounded-lg">
              <Calendar className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">زيارات اليوم</p>
              <p className="text-2xl font-bold">{analytics?.today_visits || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-white/5 rounded-xl p-6" data-testid="week-visits-card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-500/20 rounded-lg">
              <TrendingUp className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">زيارات الأسبوع</p>
              <p className="text-2xl font-bold">{analytics?.week_visits || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-white/5 rounded-xl p-6" data-testid="month-visits-card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-500/20 rounded-lg">
              <Users className="w-6 h-6 text-purple-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">زيارات الشهر</p>
              <p className="text-2xl font-bold">{analytics?.month_visits || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Visits Chart */}
        <div className="bg-card border border-white/5 rounded-xl p-6" data-testid="daily-chart">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            الزيارات اليومية (آخر 30 يوم)
          </h3>
          {analytics?.daily_visits && analytics.daily_visits.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analytics.daily_visits}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis 
                  dataKey="date" 
                  stroke="#888"
                  tick={{ fill: '#888', fontSize: 12 }}
                />
                <YAxis 
                  stroke="#888"
                  tick={{ fill: '#888', fontSize: 12 }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1a1a1a', 
                    border: '1px solid #333',
                    borderRadius: '8px'
                  }}
                  labelStyle={{ color: '#d4af37' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#d4af37" 
                  strokeWidth={2}
                  dot={{ fill: '#d4af37', strokeWidth: 2 }}
                  name="الزيارات"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              لا توجد بيانات كافية
            </div>
          )}
        </div>

        {/* Countries Chart */}
        <div className="bg-card border border-white/5 rounded-xl p-6" data-testid="countries-chart">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" />
            الزيارات حسب الدولة
          </h3>
          {analytics?.countries && analytics.countries.length > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="50%" height={250}>
                <PieChart>
                  <Pie
                    data={analytics.countries.slice(0, 5)}
                    dataKey="count"
                    nameKey="country"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                  >
                    {analytics.countries.slice(0, 5).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1a1a1a', 
                      border: '1px solid #333',
                      borderRadius: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-3">
                {analytics.countries.slice(0, 5).map((country, index) => (
                  <div key={country.country_code || `country-${index}`} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getCountryFlag(country.country_code)}</span>
                      <span>{country.country || 'غير معروف'}</span>
                    </div>
                    <span className="font-bold" style={{ color: COLORS[index % COLORS.length] }}>
                      {country.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              لا توجد بيانات كافية
            </div>
          )}
        </div>
      </div>

      {/* Pages Chart */}
      <div className="bg-card border border-white/5 rounded-xl p-6" data-testid="pages-chart">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" />
          الصفحات الأكثر زيارة
        </h3>
        {analytics?.pages && analytics.pages.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={analytics.pages} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis type="number" stroke="#888" />
              <YAxis 
                type="category" 
                dataKey="page" 
                stroke="#888" 
                width={150}
                tick={{ fill: '#888', fontSize: 12 }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1a1a1a', 
                  border: '1px solid #333',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="count" fill="#d4af37" radius={[0, 4, 4, 0]} name="الزيارات" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            لا توجد بيانات كافية
          </div>
        )}
      </div>

      {/* Recent Visits Table */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl overflow-hidden" data-testid="recent-visits-table">
        <div className="p-4 border-b border-white/5">
          <h3 className="font-semibold flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            آخر الزيارات
          </h3>
        </div>
        
        {recentVisits.length === 0 ? (
          <div className="p-12 text-center">
            <Eye className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">لا توجد زيارات مسجلة</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary/30">
                <tr>
                  <th className="p-4 text-start">الوقت</th>
                  <th className="p-4 text-start">الدولة</th>
                  <th className="p-4 text-start">المدينة</th>
                  <th className="p-4 text-start">الصفحة</th>
                  <th className="p-4 text-start">المتصفح</th>
                </tr>
              </thead>
              <tbody>
                {recentVisits.map((visit) => (
                  <tr key={visit.id || visit.timestamp} className="border-t border-white/5 hover:bg-secondary/20">
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        {formatDate(visit.timestamp)}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getCountryFlag(visit.country_code)}</span>
                        {visit.country || 'غير معروف'}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                        {visit.city || '-'}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="bg-primary/20 text-primary px-2 py-1 rounded text-sm">
                        {visit.page || '/'}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-muted-foreground truncate max-w-[200px]">
                      {visit.user_agent?.split(' ')[0] || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
